import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  MessageSquare, Plus, Pencil, Trash2, Check, X, Users, BookOpen,
  ChevronDown, ChevronRight, Sparkles
} from 'lucide-react';
import {
  reachCommsAPI, reachAudiencesAPI, reachGuideContentAPI,
  ReachCommsItem, ReachAudience, ReachGuideContent, ReachGuideContentType,
  ApprovalStatus, ReachStage
} from '../../services/reachService';
import { REACHStageBadge } from '../../components/reach/common/REACHStageBadge';

const STAGES: ReachStage[] = ['R', 'E', 'A', 'C', 'H'];
const APPROVAL_LABELS: Record<ApprovalStatus, string> = {
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
};
const APPROVAL_COLORS: Record<ApprovalStatus, string> = {
  pending: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-green-50 text-green-700 border-green-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};
const GUIDE_TYPE_LABELS: Record<ReachGuideContentType, string> = {
  overview: 'Overview',
  charter: 'Charter',
  themes: 'Themes',
  other: 'Other',
};

type PageTab = 'log' | 'audiences' | 'content';

// ── Comms form ───────────────────────────────────────────────────────────

const EMPTY: Omit<ReachCommsItem, 'id' | 'createdAt' | 'createdBy'> = {
  item: '', audienceIds: [], draftedBy: '', approver: '',
  approvalStatus: 'pending', sendDate: '', channel: '', notes: '', reachStage: 'R',
};

type FormData = Omit<ReachCommsItem, 'id' | 'createdAt' | 'createdBy'>;

interface FormModalProps {
  initial?: ReachCommsItem | null;
  audiences: ReachAudience[];
  onCreate: (data: FormData) => Promise<ReachCommsItem>;
  onUpdate: (id: string, data: Partial<ReachCommsItem>) => Promise<ReachCommsItem>;
  onClose: () => void;
  onManageAudiences: () => void;
}

const FormModal: React.FC<FormModalProps> = ({
  initial, audiences, onCreate, onUpdate, onClose, onManageAudiences
}) => {
  const [form, setForm] = useState<FormData>(initial ? {
    item: initial.item, audienceIds: initial.audienceIds ?? [], draftedBy: initial.draftedBy,
    approver: initial.approver, approvalStatus: initial.approvalStatus,
    sendDate: initial.sendDate, channel: initial.channel, notes: initial.notes ?? '',
    reachStage: initial.reachStage, draftsByAudience: initial.draftsByAudience,
  } : { ...EMPTY });
  const [savedId, setSavedId] = useState<string | null>(initial?.id ?? null);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const set = (k: keyof FormData, v: any) => setForm(f => ({ ...f, [k]: v }));

  const toggleAudience = (id: string) => {
    setForm(f => ({
      ...f,
      audienceIds: f.audienceIds.includes(id)
        ? f.audienceIds.filter(a => a !== id)
        : [...f.audienceIds, id]
    }));
  };

  const toggleExpanded = (id: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const startManualDraft = async (audienceId: string) => {
    if (!savedId) return;
    const merged = [...(form.draftsByAudience ?? []), { audienceId, draft: '', status: 'draft' as const }];
    setForm(f => ({ ...f, draftsByAudience: merged }));
    await onUpdate(savedId, { draftsByAudience: merged });
  };

  const persistDraft = async (audienceId: string, updates: Partial<{ draft: string; status: 'draft' | 'approved' }>) => {
    if (!savedId) return;
    const merged = (form.draftsByAudience ?? []).map(d =>
      d.audienceId === audienceId ? { ...d, ...updates } : d
    );
    setForm(f => ({ ...f, draftsByAudience: merged }));
    await onUpdate(savedId, { draftsByAudience: merged });
  };

  const submit = async () => {
    if (!form.item.trim() || !form.sendDate) return;
    setSaving(true);
    try {
      if (!savedId) {
        const created = await onCreate(form);
        setSavedId(created.id);
        setForm(f => ({ ...f, draftsByAudience: created.draftsByAudience }));
      } else {
        await onUpdate(savedId, form);
        onClose();
      }
    } finally {
      setSaving(false);
    }
  };

  const selectedAudiences = audiences.filter(a => form.audienceIds.includes(a.id));

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
          <h2 className="text-base font-bold text-navy">{savedId ? 'Edit Comms Item' : 'New Comms Item'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-navy"><X size={18} /></button>
        </div>
        <div className="flex">
          <div className="flex-1 min-w-0 p-5 flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Communication item <span className="text-red-500">*</span></label>
            <textarea rows={2} value={form.item} onChange={e => set('item', e.target.value)}
              placeholder="e.g. Launch communication from sponsor"
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none" />
          </div>

          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5 flex items-center justify-between">
              <span>Audiences</span>
              <button type="button" onClick={onManageAudiences} className="text-xs font-semibold text-primary hover:underline">
                Manage audiences
              </button>
            </label>
            {audiences.length === 0 ? (
              <p className="text-xs text-slate-400 border border-dashed border-slate-200 rounded-xl px-3 py-2.5">
                No audiences defined yet. Add one from the Audiences tab.
              </p>
            ) : (
              <div className="border border-[#e2e8f0] rounded-xl max-h-40 overflow-y-auto divide-y divide-[#e2e8f0]">
                {audiences.map(a => (
                  <label key={a.id} className="flex items-center gap-2 px-3 py-2 text-sm text-navy hover:bg-slate-50 cursor-pointer">
                    <input type="checkbox" checked={form.audienceIds.includes(a.id)} onChange={() => toggleAudience(a.id)}
                      className="accent-primary" />
                    <span className="font-medium">{a.name}</span>
                    {a.description && <span className="text-slate-400 text-xs truncate">— {a.description}</span>}
                  </label>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-navy mb-1.5">Channel</label>
              <input value={form.channel} onChange={e => set('channel', e.target.value)} placeholder="Email / Teams"
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-navy mb-1.5">Send date <span className="text-red-500">*</span></label>
              <input type="date" value={form.sendDate} onChange={e => set('sendDate', e.target.value)}
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-navy mb-1.5">Drafted by</label>
              <input value={form.draftedBy} onChange={e => set('draftedBy', e.target.value)} placeholder="Name"
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-navy mb-1.5">Approver</label>
              <input value={form.approver} onChange={e => set('approver', e.target.value)} placeholder="Name"
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">REACH stage</label>
            <select value={form.reachStage} onChange={e => set('reachStage', e.target.value as ReachStage)}
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white">
              {STAGES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Approval status</label>
            <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden">
              {(['pending', 'approved', 'rejected'] as ApprovalStatus[]).map(s => (
                <button key={s} type="button" onClick={() => set('approvalStatus', s)}
                  className={`flex-1 py-2 text-xs font-semibold transition-all ${
                    form.approvalStatus === s ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                  }`}>
                  {APPROVAL_LABELS[s]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Notes</label>
            <textarea rows={2} value={form.notes} onChange={e => set('notes', e.target.value)}
              placeholder="Optional context or links"
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none" />
          </div>

        </div>

        <div className="w-[340px] flex-shrink-0 border-l border-[#e2e8f0] p-5 flex flex-col gap-3 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="text-sm font-semibold text-navy flex items-center gap-1.5">
              <Sparkles size={14} className="text-primary" /> Drafts
            </label>
            <p className="mt-1 text-xs text-slate-400">Drafted by the change management skill in Claude Code (/reach-comms-draft).</p>
          </div>
          {!savedId ? (
            <p className="text-xs text-slate-400">Create the comms item to start drafting.</p>
          ) : selectedAudiences.length === 0 ? (
            <p className="text-xs text-slate-400">Select at least one audience above to draft for.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {selectedAudiences.map(a => {
                const draft = (form.draftsByAudience ?? []).find(d => d.audienceId === a.id);
                const isOpen = expanded.has(a.id);
                return (
                  <div key={a.id} className="border border-[#e2e8f0] rounded-xl overflow-hidden">
                    <button type="button" onClick={() => toggleExpanded(a.id)}
                      className="w-full flex items-center justify-between px-3 py-2.5 bg-slate-50/50 hover:bg-slate-100 transition-colors text-left">
                      <span className="flex items-center gap-2 text-sm font-semibold text-navy">
                        {isOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        {a.name}
                      </span>
                      <span className="flex items-center gap-2">
                        {draft && (
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${
                            draft.status === 'approved' ? APPROVAL_COLORS.approved : APPROVAL_COLORS.pending
                          }`}>
                            {draft.status === 'approved' ? 'Approved' : 'Draft'}
                          </span>
                        )}
                      </span>
                    </button>
                    {isOpen && (
                      <div className="p-3 flex flex-col gap-2">
                        {draft ? (
                          <>
                            <textarea rows={8} value={draft.draft}
                              onChange={e => setForm(f => ({
                                ...f,
                                draftsByAudience: (f.draftsByAudience ?? []).map(d =>
                                  d.audienceId === a.id ? { ...d, draft: e.target.value } : d
                                )
                              }))}
                              onBlur={e => persistDraft(a.id, { draft: e.target.value })}
                              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none" />
                            <div className="flex justify-end gap-2">
                              <button type="button"
                                onClick={() => persistDraft(a.id, { status: draft.status === 'approved' ? 'draft' : 'approved' })}
                                className={`flex items-center gap-1.5 rounded-lg h-8 px-3 text-xs font-bold transition-colors ${
                                  draft.status === 'approved'
                                    ? 'bg-green-50 text-green-700 border border-green-200'
                                    : 'bg-primary text-white hover:bg-primary-dark'
                                }`}>
                                <Check size={12} /> {draft.status === 'approved' ? 'Approved' : 'Approve'}
                              </button>
                            </div>
                          </>
                        ) : (
                          <div className="flex items-center justify-between">
                            <p className="text-xs text-slate-400">Not drafted yet.</p>
                            <button type="button" onClick={() => startManualDraft(a.id)}
                              className="flex items-center gap-1.5 rounded-lg h-8 px-3 border border-slate-200 bg-white text-slate-600 text-xs font-semibold hover:bg-slate-50 transition-colors">
                              <Pencil size={12} /> Write draft
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
        </div>
        <div className="px-5 py-4 border-t border-[#e2e8f0] bg-slate-50/50 flex justify-end gap-2">
          <button onClick={onClose}
            className="flex items-center gap-1.5 rounded-xl h-9 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors">
            {savedId ? 'Close' : 'Cancel'}
          </button>
          <button onClick={submit} disabled={saving || !form.item.trim() || !form.sendDate}
            className="flex items-center gap-2 rounded-xl h-9 px-5 bg-primary hover:bg-primary-dark text-white text-sm font-bold shadow-sm transition-colors disabled:opacity-60">
            <Check size={14} /> {saving ? 'Saving…' : savedId ? 'Save & close' : 'Create & continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Audiences panel ──────────────────────────────────────────────────────

type AudienceFormData = Omit<ReachAudience, 'id' | 'createdAt' | 'createdBy'>;

const AUDIENCE_EMPTY: AudienceFormData = {
  name: '', description: '', whatTheyCareAbout: '', tailoringNotes: '',
  commsPreferences: { channel: '', tone: '', frequency: '' },
};

const AudienceFormModal: React.FC<{
  initial?: ReachAudience | null;
  onSave: (data: AudienceFormData) => Promise<void>;
  onClose: () => void;
}> = ({ initial, onSave, onClose }) => {
  const [form, setForm] = useState<AudienceFormData>(initial ? {
    name: initial.name, description: initial.description,
    whatTheyCareAbout: initial.whatTheyCareAbout ?? '', tailoringNotes: initial.tailoringNotes ?? '',
    commsPreferences: { ...initial.commsPreferences },
  } : { ...AUDIENCE_EMPTY });
  const [saving, setSaving] = useState(false);

  const set = (k: keyof AudienceFormData, v: any) => setForm(f => ({ ...f, [k]: v }));
  const setPref = (k: 'channel' | 'tone' | 'frequency', v: string) =>
    setForm(f => ({ ...f, commsPreferences: { ...f.commsPreferences, [k]: v } }));

  const submit = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
          <h2 className="text-base font-bold text-navy">{initial ? 'Edit Audience' : 'New Audience'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-navy"><X size={18} /></button>
        </div>
        <div className="p-5 flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Name <span className="text-red-500">*</span></label>
            <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Frontline team leads"
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Description</label>
            <textarea rows={2} value={form.description} onChange={e => set('description', e.target.value)}
              placeholder="Who they are, where they sit in the org"
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">What they care about</label>
            <textarea rows={2} value={form.whatTheyCareAbout} onChange={e => set('whatTheyCareAbout', e.target.value)}
              placeholder="Their priorities and concerns"
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Tailoring notes</label>
            <textarea rows={2} value={form.tailoringNotes} onChange={e => set('tailoringNotes', e.target.value)}
              placeholder="How messaging should be tailored for this audience"
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-semibold text-navy mb-1.5">Channel</label>
              <input value={form.commsPreferences?.channel ?? ''} onChange={e => setPref('channel', e.target.value)} placeholder="Email"
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-navy mb-1.5">Tone</label>
              <input value={form.commsPreferences?.tone ?? ''} onChange={e => setPref('tone', e.target.value)} placeholder="Direct"
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
            </div>
            <div>
              <label className="block text-sm font-semibold text-navy mb-1.5">Frequency</label>
              <input value={form.commsPreferences?.frequency ?? ''} onChange={e => setPref('frequency', e.target.value)} placeholder="Weekly"
                className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
            </div>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-[#e2e8f0] bg-slate-50/50 flex justify-end gap-2">
          <button onClick={onClose}
            className="flex items-center gap-1.5 rounded-xl h-9 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors">
            Cancel
          </button>
          <button onClick={submit} disabled={saving || !form.name.trim()}
            className="flex items-center gap-2 rounded-xl h-9 px-5 bg-primary hover:bg-primary-dark text-white text-sm font-bold shadow-sm transition-colors disabled:opacity-60">
            <Check size={14} /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

const AudiencesPanel: React.FC<{
  projectId: string;
  audiences: ReachAudience[];
  setAudiences: React.Dispatch<React.SetStateAction<ReachAudience[]>>;
  loading: boolean;
}> = ({ projectId, audiences, setAudiences, loading }) => {
  const [modal, setModal] = useState<'new' | ReachAudience | null>(null);

  const create = async (data: AudienceFormData) => {
    const r = await reachAudiencesAPI.create(projectId, data);
    setAudiences(prev => [...prev, r.data.audience]);
    setModal(null);
  };
  const update = async (id: string, data: AudienceFormData) => {
    const r = await reachAudiencesAPI.update(projectId, id, data);
    setAudiences(prev => prev.map(a => a.id === id ? r.data.audience : a));
    setModal(null);
  };
  const remove = async (id: string) => {
    if (!window.confirm('Delete this audience?')) return;
    await reachAudiencesAPI.delete(projectId, id);
    setAudiences(prev => prev.filter(a => a.id !== id));
  };

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
      <div className="p-4 border-b border-[#e2e8f0] flex items-center justify-between">
        <p className="text-sm font-semibold text-navy">{audiences.length} audience{audiences.length === 1 ? '' : 's'}</p>
        <button onClick={() => setModal('new')}
          className="flex items-center gap-1.5 rounded-xl h-9 px-4 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm">
          <Plus size={14} /> Add audience
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center items-center py-20 text-slate-400 text-sm">Loading…</div>
      ) : audiences.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <Users size={28} className="text-slate-400" />
          </div>
          <p className="text-slate-500 text-sm">No audiences defined yet.</p>
          <p className="text-slate-400 text-xs mt-1">Define cohorts to target and tailor communications for.</p>
        </div>
      ) : (
        <div className="divide-y divide-[#e2e8f0]">
          {audiences.map(a => (
            <div key={a.id} className="flex items-start justify-between gap-4 px-4 py-3.5 hover:bg-slate-50/50 transition-colors">
              <div>
                <p className="font-semibold text-navy text-sm">{a.name}</p>
                {a.description && <p className="text-slate-500 text-xs mt-0.5">{a.description}</p>}
                {a.whatTheyCareAbout && <p className="text-slate-400 text-xs mt-1">Cares about: {a.whatTheyCareAbout}</p>}
                {(a.commsPreferences?.channel || a.commsPreferences?.tone || a.commsPreferences?.frequency) && (
                  <p className="text-slate-400 text-xs mt-1">
                    {[a.commsPreferences?.channel, a.commsPreferences?.tone, a.commsPreferences?.frequency].filter(Boolean).join(' · ')}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => setModal(a)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-navy hover:bg-slate-100 transition-colors">
                  <Pencil size={13} />
                </button>
                <button onClick={() => remove(a.id)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {modal === 'new' && <AudienceFormModal onSave={create} onClose={() => setModal(null)} />}
      {modal && modal !== 'new' && (
        <AudienceFormModal initial={modal} onSave={d => update(modal.id, d)} onClose={() => setModal(null)} />
      )}
    </div>
  );
};

// ── Content library panel ────────────────────────────────────────────────

type GuideFormData = Omit<ReachGuideContent, 'id' | 'createdAt' | 'createdBy'>;
const GUIDE_EMPTY: GuideFormData = { title: '', type: 'overview', body: '' };

const GuideContentFormModal: React.FC<{
  initial?: ReachGuideContent | null;
  onSave: (data: GuideFormData) => Promise<void>;
  onClose: () => void;
}> = ({ initial, onSave, onClose }) => {
  const [form, setForm] = useState<GuideFormData>(initial ? {
    title: initial.title, type: initial.type, body: initial.body,
  } : { ...GUIDE_EMPTY });
  const [saving, setSaving] = useState(false);

  const set = (k: keyof GuideFormData, v: any) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.title.trim() || !form.body.trim()) return;
    setSaving(true);
    try { await onSave(form); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
          <h2 className="text-base font-bold text-navy">{initial ? 'Edit Content' : 'New Content'}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-navy"><X size={18} /></button>
        </div>
        <div className="p-5 flex flex-col gap-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Title <span className="text-red-500">*</span></label>
            <input value={form.title} onChange={e => set('title', e.target.value)} placeholder="e.g. Project charter"
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none" />
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Type</label>
            <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden">
              {(Object.keys(GUIDE_TYPE_LABELS) as ReachGuideContentType[]).map(t => (
                <button key={t} type="button" onClick={() => set('type', t)}
                  className={`flex-1 py-2 text-xs font-semibold transition-all ${
                    form.type === t ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                  }`}>
                  {GUIDE_TYPE_LABELS[t]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-semibold text-navy mb-1.5">Content <span className="text-red-500">*</span></label>
            <textarea rows={10} value={form.body} onChange={e => set('body', e.target.value)}
              placeholder="Guiding material for AI-drafted communications to read from"
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none" />
          </div>
        </div>
        <div className="px-5 py-4 border-t border-[#e2e8f0] bg-slate-50/50 flex justify-end gap-2">
          <button onClick={onClose}
            className="flex items-center gap-1.5 rounded-xl h-9 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors">
            Cancel
          </button>
          <button onClick={submit} disabled={saving || !form.title.trim() || !form.body.trim()}
            className="flex items-center gap-2 rounded-xl h-9 px-5 bg-primary hover:bg-primary-dark text-white text-sm font-bold shadow-sm transition-colors disabled:opacity-60">
            <Check size={14} /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

const GuideContentPanel: React.FC<{
  projectId: string;
  guideContent: ReachGuideContent[];
  setGuideContent: React.Dispatch<React.SetStateAction<ReachGuideContent[]>>;
  loading: boolean;
}> = ({ projectId, guideContent, setGuideContent, loading }) => {
  const [modal, setModal] = useState<'new' | ReachGuideContent | null>(null);

  const create = async (data: GuideFormData) => {
    const r = await reachGuideContentAPI.create(projectId, data);
    setGuideContent(prev => [...prev, r.data.content]);
    setModal(null);
  };
  const update = async (id: string, data: GuideFormData) => {
    const r = await reachGuideContentAPI.update(projectId, id, data);
    setGuideContent(prev => prev.map(g => g.id === id ? r.data.content : g));
    setModal(null);
  };
  const remove = async (id: string) => {
    if (!window.confirm('Delete this content item?')) return;
    await reachGuideContentAPI.delete(projectId, id);
    setGuideContent(prev => prev.filter(g => g.id !== id));
  };

  return (
    <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
      <div className="p-4 border-b border-[#e2e8f0] flex items-center justify-between">
        <p className="text-sm font-semibold text-navy">{guideContent.length} content item{guideContent.length === 1 ? '' : 's'}</p>
        <button onClick={() => setModal('new')}
          className="flex items-center gap-1.5 rounded-xl h-9 px-4 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm">
          <Plus size={14} /> Add content
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center items-center py-20 text-slate-400 text-sm">Loading…</div>
      ) : guideContent.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <BookOpen size={28} className="text-slate-400" />
          </div>
          <p className="text-slate-500 text-sm">No guiding content yet.</p>
          <p className="text-slate-400 text-xs mt-1">Add a project overview, charter, or themes for AI drafting to reference.</p>
        </div>
      ) : (
        <div className="divide-y divide-[#e2e8f0]">
          {guideContent.map(g => (
            <div key={g.id} className="flex items-start justify-between gap-4 px-4 py-3.5 hover:bg-slate-50/50 transition-colors">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-navy text-sm">{g.title}</p>
                  <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                    {GUIDE_TYPE_LABELS[g.type]}
                  </span>
                </div>
                <p className="text-slate-400 text-xs mt-1 line-clamp-2 max-w-2xl">{g.body}</p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button onClick={() => setModal(g)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-navy hover:bg-slate-100 transition-colors">
                  <Pencil size={13} />
                </button>
                <button onClick={() => remove(g.id)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {modal === 'new' && <GuideContentFormModal onSave={create} onClose={() => setModal(null)} />}
      {modal && modal !== 'new' && (
        <GuideContentFormModal initial={modal} onSave={d => update(modal.id, d)} onClose={() => setModal(null)} />
      )}
    </div>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────

export const CommsLogPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [tab, setTab] = useState<PageTab>('log');
  const [items, setItems] = useState<ReachCommsItem[]>([]);
  const [audiences, setAudiences] = useState<ReachAudience[]>([]);
  const [guideContent, setGuideContent] = useState<ReachGuideContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState<'new' | ReachCommsItem | null>(null);
  const [stageFilter, setStageFilter] = useState<ReachStage | 'all'>('all');

  useEffect(() => {
    if (!projectId) return;
    Promise.all([
      reachCommsAPI.list(projectId),
      reachAudiencesAPI.list(projectId),
      reachGuideContentAPI.list(projectId),
    ]).then(([comms, aud, guide]) => {
      setItems(comms.data.comms);
      setAudiences(aud.data.audiences);
      setGuideContent(guide.data.guideContent);
      setLoading(false);
    });
  }, [projectId]);

  const audienceNames = useMemo(() => {
    const map = new Map(audiences.map(a => [a.id, a.name]));
    return (ids: string[]) => ids.map(id => map.get(id) ?? id);
  }, [audiences]);

  const filtered = stageFilter === 'all' ? items : items.filter(i => i.reachStage === stageFilter);
  const sorted = [...filtered].sort((a, b) => a.sendDate.localeCompare(b.sendDate));

  const create = async (data: FormData) => {
    if (!projectId) throw new Error('Missing projectId');
    const r = await reachCommsAPI.create(projectId, data);
    setItems(prev => [...prev, r.data.item]);
    return r.data.item;
  };

  const update = async (id: string, data: Partial<ReachCommsItem>) => {
    if (!projectId) throw new Error('Missing projectId');
    const r = await reachCommsAPI.update(projectId, id, data);
    setItems(prev => prev.map(i => i.id === id ? r.data.item : i));
    return r.data.item;
  };

  const remove = async (id: string) => {
    if (!projectId || !window.confirm('Delete this comms item?')) return;
    await reachCommsAPI.delete(projectId, id);
    setItems(prev => prev.filter(i => i.id !== id));
  };

  if (!projectId) return null;

  const renderTableContent = () => {
    if (loading) {
      return <div className="flex justify-center items-center py-20 text-slate-400 text-sm">Loading…</div>;
    }

    if (sorted.length === 0) {
      return (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
            <MessageSquare size={28} className="text-slate-400" />
          </div>
          <p className="text-slate-500 text-sm">No comms items yet.</p>
          <p className="text-slate-400 text-xs mt-1">Add planned communications to track drafting and approval.</p>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Communication</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Audiences</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Channel</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Send date</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Stage</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-slate-500">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-[#e2e8f0]">
            {sorted.map(item => (
              <tr key={item.id} className="hover:bg-slate-50/50 transition-colors">
                <td className="px-4 py-3">
                  <p className="font-medium text-navy text-sm leading-snug max-w-xs">{item.item}</p>
                  {item.notes && <p className="text-xs text-slate-400 mt-0.5 truncate max-w-xs">{item.notes}</p>}
                </td>
                <td className="px-4 py-3 text-slate-600 text-sm whitespace-nowrap max-w-xs truncate">
                  {item.audienceIds?.length ? audienceNames(item.audienceIds).join(', ') : '—'}
                </td>
                <td className="px-4 py-3 text-slate-600 text-sm whitespace-nowrap">{item.channel || '—'}</td>
                <td className="px-4 py-3 text-slate-600 text-sm whitespace-nowrap">{item.sendDate}</td>
                <td className="px-4 py-3"><REACHStageBadge stage={item.reachStage} /></td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${APPROVAL_COLORS[item.approvalStatus]}`}>
                    {APPROVAL_LABELS[item.approvalStatus]}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 justify-end">
                    <button onClick={() => setModal(item)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-navy hover:bg-slate-100 transition-colors">
                      <Pencil size={13} />
                    </button>
                    <button onClick={() => remove(item.id)}
                      className="p-1.5 rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                      <Trash2 size={13} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const TABS: { key: PageTab; label: string; icon: React.ReactNode }[] = [
    { key: 'log', label: 'Communications', icon: <MessageSquare size={14} /> },
    { key: 'audiences', label: 'Audiences', icon: <Users size={14} /> },
    { key: 'content', label: 'Content Library', icon: <BookOpen size={14} /> },
  ];

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">
        <header className="flex items-center gap-4 border-b border-slate-200 pb-6">
          <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
            <MessageSquare size={22} />
          </div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-navy">Communications</h1>
            <p className="text-slate-500 text-sm font-medium">{items.length} communications planned</p>
          </div>
          {tab === 'log' && (
            <div className="ml-auto">
              <button onClick={() => setModal('new')}
                className="flex items-center gap-2 rounded-xl h-10 px-6 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm">
                <Plus size={16} /> Add comms item
              </button>
            </div>
          )}
        </header>

        <div className="flex items-center gap-2">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-1.5 px-3.5 py-2 rounded-xl text-sm font-semibold transition-all ${
                tab === t.key ? 'bg-navy text-white' : 'bg-white text-slate-500 border border-[#e2e8f0] hover:bg-slate-50'
              }`}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {tab === 'log' && (
          <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-[#e2e8f0] flex items-center gap-3 flex-wrap">
              <span className="text-xs font-semibold text-slate-500">Stage:</span>
              {(['all', ...STAGES] as const).map(s => (
                <button key={s} onClick={() => setStageFilter(s)}
                  className={`px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                    stageFilter === s ? 'bg-primary text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                  }`}>
                  {s === 'all' ? 'All' : s}
                </button>
              ))}
            </div>

            {renderTableContent()}
          </div>
        )}

        {tab === 'audiences' && (
          <AudiencesPanel projectId={projectId} audiences={audiences} setAudiences={setAudiences} loading={loading} />
        )}

        {tab === 'content' && (
          <GuideContentPanel projectId={projectId} guideContent={guideContent} setGuideContent={setGuideContent} loading={loading} />
        )}
      </div>

      {modal === 'new' && (
        <FormModal
          audiences={audiences}
          onCreate={create}
          onUpdate={update}
          onClose={() => setModal(null)}
          onManageAudiences={() => { setModal(null); setTab('audiences'); }}
        />
      )}
      {modal && modal !== 'new' && (
        <FormModal
          initial={modal}
          audiences={audiences}
          onCreate={create}
          onUpdate={update}
          onClose={() => setModal(null)}
          onManageAudiences={() => { setModal(null); setTab('audiences'); }}
        />
      )}
    </div>
  );
};

export default CommsLogPage;
